import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { AlertCircle, LoaderCircle, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { canManageFactory } from "@/app/guards";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ChipsRelacion } from "@/components/ui/ChipsRelacion";
import { DataTable } from "@/components/ui/data-table";
import { ExportButton } from "@/components/ui/ExportButton";
import { type CampoFiltro, FiltrosBar } from "@/components/ui/filtros-bar";
import { ImportButton } from "@/components/ui/ImportButton";
import { Modal } from "@/components/ui/modal";
import { ApiError } from "@/lib/api";
import { useAreas } from "@/lib/areas";
import { useMe } from "@/lib/auth";
import {
  type Criticidad,
  type Equipo,
  type EquipoEstado,
  useCreateEquipo,
  useDeleteEquipo,
  useEquipos,
  useUpdateEquipo,
} from "@/lib/equipos";
import { AVISO_FILTRADO_LOCAL, mensajeErrorLista, useFiltrosURL } from "@/lib/filtros";
import { CLAVES_EQUIPOS } from "@/lib/filtros/equipos";
import { EquipoForm, type EquipoFormValues } from "./EquipoForm";

// Etiqueta y color del estado calculado. Es binario (#243): describe si hay un candado
// puesto en el equipo, nada más. `activo` y `loteable` van en sus propias columnas.
const ESTADO_META: Record<EquipoEstado, { label: string; className: string }> = {
  DESBLOQUEADO: { label: "Desbloqueado", className: "bg-green-100 text-green-800" },
  BLOQUEADO: { label: "Bloqueado", className: "bg-red-100 text-red-800" },
};

const CRITICIDAD_LABEL: Record<Criticidad, string> = {
  ALTA: "Alta",
  MEDIA: "Media",
  BAJA: "Baja",
};

// toInput normaliza los valores del formulario al payload del API (descripción vacía ⇒ null).
function toInput(values: EquipoFormValues) {
  const descripcion = values.descripcion.trim();
  return {
    nombre_equipo: values.nombre_equipo.trim(),
    descripcion: descripcion === "" ? null : descripcion,
    id_area: values.id_area,
    criticidad: values.criticidad,
    loteable: values.loteable,
    activo: values.activo,
  };
}

/**
 * EquiposPage lista los equipos con su código EQ-XXXX, área, criticidad y estado
 * calculado, y ofrece el CRUD completo (alta/edición en modal, borrado con confirmación
 * y protección de historial). La tabla usa el DataTable de react-table (#34) SIN buscador en
 * memoria (`sinBuscador`): el texto libre y los filtros por área y estado son del SERVIDOR y
 * viven en la FiltrosBar (#247), y la tabla solo ordena y pagina, así que sus `accessorFn`
 * alimentan la celda, la ordenación o ambas, nunca una búsqueda (#346). Además exporta e
 * importa xlsx.
 */
export function EquiposPage() {
  const { data: me } = useMe();
  const obraId = useObraId();
  const puedeGestionar = me != null && canManageFactory(me, obraId);

  // Los filtros viven en la URL (#247): compartible, recargable y con el servidor filtrando.
  const { filtros, setFiltros } = useFiltrosURL(CLAVES_EQUIPOS);
  const {
    data: equipos,
    isPending,
    isError,
    baseVacia,
    avisoSinConexion,
    codigoError,
    camposError,
  } = useEquipos(obraId, filtros);
  const { data: areas } = useAreas(obraId);
  const crear = useCreateEquipo(obraId);
  const editar = useUpdateEquipo(obraId);
  const borrar = useDeleteEquipo(obraId);

  const [formState, setFormState] = useState<
    { mode: "nueva" } | { mode: "editar"; equipo: Equipo } | null
  >(null);
  const [aBorrar, setABorrar] = useState<Equipo | null>(null);

  const listaAreas = areas ?? [];
  const sinAreas = listaAreas.length === 0;

  const columns = useMemo<ColumnDef<Equipo, unknown>[]>(() => {
    const cols: ColumnDef<Equipo, unknown>[] = [
      {
        accessorKey: "codigo_equipo",
        header: "Código",
        cell: ({ row }) => (
          <span className="font-medium text-slate-900 tabular-nums">
            {row.original.codigo_equipo}
          </span>
        ),
      },
      {
        accessorKey: "nombre_equipo",
        header: "Nombre",
        cell: ({ row }) => <span className="text-slate-900">{row.original.nombre_equipo}</span>,
      },
      {
        id: "area",
        accessorFn: (e) => e.nombre_area,
        header: "Área",
        cell: ({ row }) => (
          <Link
            to="/obras/$obraId/areas"
            params={{ obraId: String(obraId) }}
            search={{ q: row.original.nombre_area }}
            aria-label={`Ver el área ${row.original.nombre_area}`}
            className="font-medium text-primary-700 hover:underline"
          >
            {row.original.nombre_area}
          </Link>
        ),
        meta: { stopRowClick: true },
      },
      {
        id: "criticidad",
        accessorFn: (e) => CRITICIDAD_LABEL[e.criticidad],
        header: "Criticidad",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden md:table-cell" },
      },
      {
        id: "loteable",
        accessorFn: (e) => (e.loteable ? "Sí" : "No"),
        header: "Loteable",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden md:table-cell" },
      },
      // «Activo» es un ATRIBUTO del catálogo, no un estado (#243): tiene su propia columna
      // junto a «Loteable» en vez de robarle el badge al estado de seguridad.
      {
        id: "activo",
        accessorFn: (e) => (e.activo ? "Sí" : "No"),
        header: "Activo",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden md:table-cell" },
      },
      {
        id: "estado",
        accessorFn: (e) => ESTADO_META[e.estado].label,
        header: "Estado",
        cell: ({ row }) => (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 font-medium text-sm ${ESTADO_META[row.original.estado].className}`}
          >
            {ESTADO_META[row.original.estado].label}
          </span>
        ),
      },
      // Relación cruzada (#248): QUÉ bloquea el equipo. Candados, puntos y LOTOs se muestran
      // completos, con un enlace propio a cada entidad. El accessorFn devuelve los códigos de
      // candado concatenados para que la columna ordene por ellos.
      {
        id: "bloqueo",
        accessorFn: (e) => e.candados.map((c) => c.codigo_candado).join(" "),
        header: "Bloqueo",
        meta: { stopRowClick: true },
        cell: ({ row }) => {
          const { candados, puntos_bloqueados: puntos } = row.original;
          const lotos = candados.filter(
            (candado, indice) =>
              candados.findIndex((otro) => otro.id_loto === candado.id_loto) === indice,
          );
          if (candados.length === 0) {
            return <span className="text-slate-500">Sin bloqueo</span>;
          }
          return (
            <div className="flex min-w-48 flex-col gap-2">
              <div className="flex items-start gap-1.5">
                <span className="min-w-18 pt-0.5 text-slate-500 text-xs">Candados</span>
                <ChipsRelacion
                  vacio="Sin candados"
                  visibles={candados.length}
                  items={candados.map((candado) => ({
                    clave: candado.id_candado,
                    etiqueta: candado.codigo_candado,
                    titulo: `Ver el candado ${candado.codigo_candado}`,
                    enlace: {
                      to: "/obras/$obraId/candados",
                      params: { obraId: String(obraId) },
                      search: { q: candado.codigo_candado },
                    },
                  }))}
                />
              </div>
              <div className="flex items-start gap-1.5">
                <MapPin className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                <span className="min-w-13 pt-0.5 text-slate-500 text-xs">Puntos</span>
                <ChipsRelacion
                  vacio="Sin puntos bloqueados"
                  visibles={puntos.length}
                  items={puntos.map((punto) => ({
                    clave: punto.id_punto,
                    etiqueta: punto.nombre,
                    titulo: `Ver el punto ${punto.nombre}`,
                    enlace: {
                      to: "/obras/$obraId/puntos",
                      params: { obraId: String(obraId) },
                      search: { equipo: String(row.original.id), q: punto.nombre },
                    },
                  }))}
                />
              </div>
              <div className="flex items-start gap-1.5">
                <span className="min-w-18 pt-0.5 text-slate-500 text-xs">LOTOs</span>
                <ChipsRelacion
                  vacio="Sin LOTO"
                  visibles={lotos.length}
                  items={lotos.map((loto) => ({
                    clave: loto.id_loto,
                    etiqueta: loto.numero_loto,
                    titulo: `Abrir ${loto.numero_loto}`,
                    enlace: {
                      to: "/obras/$obraId/lotos/$id",
                      params: { obraId: String(obraId), id: String(loto.id_loto) },
                    },
                  }))}
                />
              </div>
            </div>
          );
        },
      },
    ];
    if (puedeGestionar) {
      cols.push({
        id: "acciones",
        header: () => <span className="sr-only">Acciones</span>,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Editar ${row.original.nombre_equipo}`}
              onClick={() => setFormState({ mode: "editar", equipo: row.original })}
            >
              <Pencil className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Eliminar ${row.original.nombre_equipo}`}
              onClick={() => setABorrar(row.original)}
            >
              <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
            </Button>
          </div>
        ),
      });
    }
    return cols;
  }, [obraId, puedeGestionar]);

  // Campos de la barra de filtros. Los valores son los del API (ids y enums), no las
  // etiquetas: es lo que viaja a la URL y al servidor. Las áreas salen del listado
  // relacionado que la página ya carga.
  const campos = useMemo<CampoFiltro[]>(
    () => [
      { tipo: "texto", nombre: "q", etiqueta: "Buscar", placeholder: "Buscar equipos…" },
      {
        tipo: "select",
        nombre: "area",
        etiqueta: "Área",
        opciones: listaAreas.map((a) => ({ valor: String(a.id), etiqueta: a.nombre })),
      },
      {
        tipo: "select",
        nombre: "criticidad",
        etiqueta: "Criticidad",
        opciones: (["ALTA", "MEDIA", "BAJA"] as const).map((c) => ({
          valor: c,
          etiqueta: CRITICIDAD_LABEL[c],
        })),
      },
      {
        tipo: "select",
        nombre: "estado",
        etiqueta: "Estado",
        opciones: (["BLOQUEADO", "DESBLOQUEADO"] as const).map((e) => ({
          valor: e,
          etiqueta: ESTADO_META[e].label,
        })),
      },
      { tipo: "booleano", nombre: "activo", etiqueta: "Activo" },
      { tipo: "booleano", nombre: "loteable", etiqueta: "Loteable" },
    ],
    [listaAreas],
  );

  const filas = equipos ?? [];
  // «Aún no hay equipos» solo cuando el catálogo está vacío de verdad; si hay equipos pero
  // los filtros no dejan ninguno, la tabla lo dice por su fila vacía.

  function cerrarForm() {
    setFormState(null);
  }

  async function onSubmitForm(values: EquipoFormValues) {
    if (formState?.mode === "editar") {
      await editar.mutateAsync({ id: formState.equipo.id, input: toInput(values) });
    } else {
      await crear.mutateAsync(toInput(values));
    }
    cerrarForm();
  }

  async function confirmarBorrado() {
    if (!aBorrar) {
      return;
    }
    try {
      await borrar.mutateAsync(aBorrar.id);
      setABorrar(null);
    } catch {
      // El fallo (p. ej. 409 EQUIPO_CON_HISTORIAL) se refleja en borrar.error y se
      // muestra en el modal; se mantiene abierto para que el usuario lo lea.
    }
  }

  // Mensaje del error de borrado: distingue el candado puesto (#243) y la protección de
  // historial (409) del fallo genérico.
  const codigoBorrado = borrar.error instanceof ApiError ? borrar.error.code : null;
  const mensajeBorrado =
    codigoBorrado === "EQUIPO_EN_USO"
      ? "El equipo tiene un candado puesto en alguno de sus puntos. Retira el candado antes de eliminarlo."
      : codigoBorrado === "EQUIPO_CON_HISTORIAL"
        ? "El equipo tiene fuentes, puntos o procedimientos asociados. Desactívalo en vez de eliminarlo."
        : "No se pudo eliminar el equipo. Inténtalo de nuevo.";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Equipos</h1>
          <p className="text-base text-slate-500">Máquinas y activos de fábrica.</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <ImportButton tipo="equipos" etiqueta="equipos" obraId={obraId} />
          <ExportButton tipo="equipos" obraId={obraId} filtros={filtros} />
          {puedeGestionar ? (
            <Button onClick={() => setFormState({ mode: "nueva" })} disabled={sinAreas}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Nuevo equipo
            </Button>
          ) : null}
        </div>
      </div>

      {puedeGestionar && sinAreas ? (
        <Alert variant="info">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>Crea primero un área para poder dar de alta equipos.</span>
        </Alert>
      ) : null}

      {isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>
            {mensajeErrorLista(
              { codigoError, camposError },
              "No se pudieron cargar los equipos. Inténtalo de nuevo.",
            )}
          </span>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando equipos…
        </div>
      ) : null}

      {!isPending && !isError && baseVacia ? (
        <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
          Aún no hay equipos.
          {puedeGestionar && !sinAreas ? " Crea el primero con «Nuevo equipo»." : ""}
        </div>
      ) : null}

      {!baseVacia ? (
        <>
          <FiltrosBar
            campos={campos}
            valor={filtros}
            onChange={setFiltros}
            aviso={avisoSinConexion ? AVISO_FILTRADO_LOCAL : undefined}
          />
          {!isPending ? (
            <DataTable
              columns={columns}
              data={filas}
              sinBuscador
              onRowClick={
                puedeGestionar ? (equipo) => setFormState({ mode: "editar", equipo }) : undefined
              }
            />
          ) : null}
        </>
      ) : null}

      {/* Alta / edición */}
      <Modal
        open={formState !== null}
        onClose={cerrarForm}
        title={formState?.mode === "editar" ? "Editar equipo" : "Nuevo equipo"}
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto"
      >
        {formState !== null ? (
          <>
            {formState.mode === "editar" ? (
              <section aria-labelledby="puntos-bloqueados-del-equipo">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-slate-500" aria-hidden="true" />
                  <h3 id="puntos-bloqueados-del-equipo" className="font-semibold text-slate-900">
                    Puntos bloqueados
                  </h3>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600 text-sm">
                    {formState.equipo.puntos_bloqueados.length}
                  </span>
                </div>
                <div className="mt-3">
                  <ChipsRelacion
                    vacio="Sin puntos bloqueados"
                    visibles={formState.equipo.puntos_bloqueados.length}
                    items={formState.equipo.puntos_bloqueados.map((punto) => ({
                      clave: punto.id_punto,
                      etiqueta: punto.nombre,
                      titulo: `Ver el punto ${punto.nombre}`,
                      enlace: {
                        to: "/obras/$obraId/puntos",
                        params: { obraId: String(obraId) },
                        search: {
                          equipo: String(formState.equipo.id),
                          q: punto.nombre,
                        },
                      },
                    }))}
                  />
                </div>
              </section>
            ) : null}
            <EquipoForm
              areas={listaAreas}
              codigo={formState.mode === "editar" ? formState.equipo.codigo_equipo : undefined}
              defaultValues={
                formState.mode === "editar"
                  ? {
                      nombre_equipo: formState.equipo.nombre_equipo,
                      descripcion: formState.equipo.descripcion ?? "",
                      id_area: formState.equipo.id_area,
                      criticidad: formState.equipo.criticidad,
                      loteable: formState.equipo.loteable,
                      activo: formState.equipo.activo,
                    }
                  : {
                      nombre_equipo: "",
                      descripcion: "",
                      id_area: listaAreas[0]?.id ?? 0,
                      criticidad: "MEDIA",
                      loteable: true,
                      activo: true,
                    }
              }
              submitLabel={formState.mode === "editar" ? "Guardar cambios" : "Crear equipo"}
              pending={crear.isPending || editar.isPending}
              onSubmit={onSubmitForm}
              onCancel={cerrarForm}
            />
          </>
        ) : null}
      </Modal>

      {/* Confirmación de borrado */}
      <Modal open={aBorrar !== null} onClose={() => setABorrar(null)} title="Eliminar equipo">
        <p className="text-base text-slate-600">
          ¿Seguro que quieres eliminar el equipo <strong>{aBorrar?.nombre_equipo}</strong>? Esta
          acción lo da de baja.
        </p>
        {borrar.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{mensajeBorrado}</span>
          </Alert>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setABorrar(null)} disabled={borrar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmarBorrado()}
            disabled={borrar.isPending}
          >
            {borrar.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            )}
            Eliminar
          </Button>
        </div>
      </Modal>
    </div>
  );
}
